import { AttributeCard } from "@/components/Card/AttributeCard";
import { defaultNS, useTranslation } from "@/lib/i18n";

import { DropZone } from "@/components/DropZone";
import { apiClient } from "@/lib/api/client";
import { getPath } from "@/lib/api/path";
import { convertSize } from "@/utils/number/si-prefix";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { type FileWithPath, useDropzone } from "react-dropzone";

type Props = {
  setUploadedFileKey: (key: string | undefined) => void;
};

const UploadFileDropZoneCard = ({ setUploadedFileKey }: Props) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.person.importCsv.prepareCsvFile",
  });
  const [acceptedFiles, setAcceptedFiles] = useState<FileWithPath[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isUploaded, setIsUploaded] = useState(false);

  const onDrop = (files: FileWithPath[]) => {
    if (files.length > 1) return;
    if (files[0].size >= convertSize(10, "MB", "B")) {
      setError(t("fileSizeError"));
      return;
    }
    setError(undefined);
    setAcceptedFiles(files);
    mutate(files[0]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "text/csv": [".csv"],
    },
    noClick: isUploaded,
  });

  const { mutate } = useMutation({
    mutationKey: [getPath("/v2/s3/presigned-url/import-csv")],
    mutationFn: async (file: FileWithPath) => {
      const data = await apiClient.GET(
        getPath("/v2/s3/presigned-url/import-csv"),
        {
          params: { query: { filename: file.name } },
        },
      );

      if (!data.data || !data.data.url) {
        throw new Error("Failed to get presigned URL");
      }

      await fetch(data.data.url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": "text/csv",
        },
      });

      return data;
    },
    onSuccess: (data) => {
      setIsUploaded(true);
      setUploadedFileKey(data.data.key);
    },
  });

  const onResetButtonClick = () => {
    setIsUploaded(false);
    setError(undefined);
    setUploadedFileKey(undefined);
    setAcceptedFiles([]);
  };

  return (
    <AttributeCard
      title={t("uploadFileHere")}
      titleStyle="CardHeader"
      className="w-full"
    >
      <DropZone
        variant="csv"
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        isDragActive={isDragActive}
        acceptedFiles={acceptedFiles}
        onResetButtonClick={onResetButtonClick}
        uploadCompleted={isUploaded}
        uploadError={error}
      />
    </AttributeCard>
  );
};

export default UploadFileDropZoneCard;
